import { useCallback, useState, useRef } from 'react';

type UseWidthResizeProps = {
  minWidth: number;
  maxWidth?: number;
  initialWidth?: number;
};

type UseWidthResizeReturn = {
  width: number;
  isResizing: boolean;
  startResize: (e: React.MouseEvent) => void;
};

export const useWidthResize = ({ minWidth, maxWidth = 800, initialWidth = minWidth }: UseWidthResizeProps): UseWidthResizeReturn => {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(initialWidth);
  const rafRef = useRef<number>();

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = moveEvent.clientX - startXRef.current;
        const newWidth = startWidthRef.current + deltaX;

        const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        setWidth(clampedWidth);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, minWidth, maxWidth]);

  return { width, isResizing, startResize };
};

type UseHeightResizeProps = {
  minHeight: number;
  maxHeight?: number;
  initialHeight?: number;
};

type UseHeightResizeReturn = {
  height: number;
  isResizing: boolean;
  startResize: (e: React.MouseEvent) => void;
};

export const useHeightResize = ({ minHeight, maxHeight = 800, initialHeight = minHeight }: UseHeightResizeProps): UseHeightResizeReturn => {
  const [height, setHeight] = useState(initialHeight);
  const [isResizing, setIsResizing] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(initialHeight);
  const rafRef = useRef<number>();

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaY = moveEvent.clientY - startYRef.current;
        const newHeight = startHeightRef.current - deltaY;

        const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        setHeight(clampedHeight);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight, maxHeight]);

  return { height, isResizing, startResize };
};
